import { notify } from '@/lib/toast';
import { useState, useCallback } from 'react';
import { RecordLink } from '@/components/records/RecordLink';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import { Gift, Plus, RefreshCw, Ban, Search, CheckCircle, XCircle, History } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuthStore } from '@/stores/auth.store';
import { formatCurrency, formatDate, formatDateOnly } from '@/lib/utils';
import api from '@/lib/api';
import { PageHeader } from '@/components/ui/page-header';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { StatusBadge } from '@/components/ui/status-badge';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { FormDialog } from '@/components/ui/form-dialog';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';

/** Gift card row returned by GET /gift-cards and GET /gift-cards/:code. */
interface GiftCard {
  id: string;
  code: string;
  initialValue: number;
  currentBalance: number;
  isActive: boolean;
  expiryDate?: string | null;
  // POS-I96: the buyer / recipient of the card.
  customer?: { id: string; name: string; phone?: string | null } | null;
}

interface CustomerOption { id: string; name: string; phone?: string | null }

/** One row of GET /gift-cards/:code/transactions (T-483). */
interface GiftCardTx {
  id: string;
  type: 'ISSUE' | 'REDEEM' | 'RELOAD' | 'REFUND' | 'VOID';
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  notes?: string | null;
  createdAt: string;
  sale?: { id: string; invoiceNumber: string } | null;
}

interface GiftCardTxPage {
  data: GiftCardTx[];
  total: number;
}

export function GiftCardsPage() {
  const { t } = useTranslation('settings');
  const { hasPermission } = useAuthStore();
  const canCreate = hasPermission('gift-cards:create') || hasPermission('gift-cards:*');
  const canDelete = hasPermission('gift-cards:delete') || hasPermission('gift-cards:*');

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [issueOpen, setIssueOpen] = useState(false);
  const [reloadOpen, setReloadOpen] = useState<string | null>(null);
  const [voidConfirm, setVoidConfirm] = useState<string | null>(null);
  const [voidForm, setVoidForm] = useState({ reason: '', refund: false });
  const [issueForm, setIssueForm] = useState({ code: '', initialValue: '', expiryDate: '', notes: '', paymentMethod: 'CASH', isComplimentary: false, issuedToCustomerId: '' });
  const [reloadAmount, setReloadAmount] = useState('');
  const [reloadMethod, setReloadMethod] = useState('CASH');
  const [lookupCode, setLookupCode] = useState('');
  const [lookedUp, setLookedUp] = useState<GiftCard | null>(null);
  const [historyCode, setHistoryCode] = useState<string | null>(null);
  const [historyPage, setHistoryPage] = useState(1);

  const qc = useQueryClient();

  const handleGiftCardUpdate = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['gift-cards'] });
  }, [qc]);
  useSocketEvent('sale.created', handleGiftCardUpdate);

  const { data, isLoading } = useQuery({
    queryKey: ['gift-cards', page, search],
    queryFn: () => api.get('/gift-cards', { params: { page, limit: 20, search: search || undefined } }).then(r => r.data),
  });

  const { data: history, isLoading: historyLoading } = useQuery<GiftCardTxPage>({
    queryKey: ['gift-card-transactions', historyCode, historyPage],
    queryFn: () =>
      api
        .get(`/gift-cards/${encodeURIComponent(historyCode!)}/transactions`, { params: { page: historyPage, limit: 10 } })
        .then((r) => r.data),
    enabled: !!historyCode,
  });

  // POS-I96: optional buyer / recipient of a new card (searched by name or phone).
  const [customerQuery, setCustomerQuery] = useState('');
  const { data: customerOptions = [] } = useQuery<CustomerOption[]>({
    queryKey: ['gift-card-customers', customerQuery],
    queryFn: () =>
      api.get('/customers', { params: { search: customerQuery || undefined, limit: 20 } }).then((r) => {
        const d = r.data as CustomerOption[] | { data?: CustomerOption[] };
        return Array.isArray(d) ? d : d?.data ?? [];
      }),
    enabled: issueOpen,
  });

  const issueMutation = useMutation({
    mutationFn: () => api.post('/gift-cards', {
      ...issueForm,
      initialValue: parseFloat(issueForm.initialValue),
      code: issueForm.code || undefined,
      expiryDate: issueForm.expiryDate || undefined,
      issuedToCustomerId: issueForm.issuedToCustomerId || undefined, // POS-I96
      // A complimentary card debits an expense head; a paid card debits the tender (T-219).
      paymentMethod: issueForm.isComplimentary ? undefined : issueForm.paymentMethod,
    }),
    onSuccess: () => {
      toast.success(t('gcCardIssued'));
      qc.invalidateQueries({ queryKey: ['gift-cards'] });
      qc.invalidateQueries({ queryKey: ['gift-card-transactions'] });
      setIssueOpen(false);
      setIssueForm({ code: '', initialValue: '', expiryDate: '', notes: '', paymentMethod: 'CASH', isComplimentary: false, issuedToCustomerId: '' });
    },
    onError: (err) => notify.error(err, t('gcFailedToIssue')),
  });

  const reloadMutation = useMutation({
    mutationFn: (code: string) => api.post(`/gift-cards/${code}/reload`, { amount: parseFloat(reloadAmount), paymentMethod: reloadMethod }),
    onSuccess: () => {
      toast.success(t('gcCardReloaded'));
      qc.invalidateQueries({ queryKey: ['gift-cards'] });
      qc.invalidateQueries({ queryKey: ['gift-card-transactions'] }); // POS-I95: History must show the new row
      setReloadOpen(null);
      setReloadAmount('');
      setReloadMethod('CASH');
    },
    onError: (err) => notify.error(err, t('gcFailedToReload')),
  });

  const voidMutation = useMutation({
    mutationFn: (code: string) =>
      api.delete(`/gift-cards/${code}/void`, { data: { reason: voidForm.reason.trim(), refund: voidForm.refund } }),
    onSuccess: () => {
      toast.success(t('gcCardVoided'));
      qc.invalidateQueries({ queryKey: ['gift-cards'] });
      qc.invalidateQueries({ queryKey: ['gift-card-transactions'] });
      setLookedUp(null);
      setVoidConfirm(null);
      setVoidForm({ reason: '', refund: false });
    },
    onError: (err) => notify.error(err, t('gcFailedToVoid')),
  });

  const handleLookup = async () => {
    if (!lookupCode) return;
    try {
      const res = await api.get(`/gift-cards/${lookupCode.toUpperCase()}`);
      setLookedUp(res.data);
    } catch (err: unknown) {
      notify.error(err, t('gcCardNotFound'));
      setLookedUp(null);
    }
  };

  const cards: GiftCard[] = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('giftCardsTitle')}
        actions={canCreate ? (
          <Button onClick={() => setIssueOpen(true)}>
            <Plus className="h-4 w-4 me-2" /> {t('issueGiftCard')}
          </Button>
        ) : undefined}
      />

      {/* Lookup card */}
      <Card>
        <CardHeader><CardTitle className="text-base">{t('gcQuickLookup')}</CardTitle></CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder={t('gcEnterCode')}
              value={lookupCode}
              onChange={e => setLookupCode(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && handleLookup()}
              className="max-w-xs font-mono"
            />
            <Button variant="secondary" onClick={handleLookup}><Search className="h-4 w-4" /></Button>
          </div>
          {lookedUp && (
            <div className="mt-3 p-3 rounded-md border bg-muted text-sm space-y-1">
              <div className="flex justify-between">
                <span className="font-mono font-bold">{lookedUp.code}</span>
                {lookedUp.isActive
                  ? <span className="text-success-text flex items-center gap-1"><CheckCircle className="h-4 w-4" />{t('gcActive')}</span>
                  : <span className="text-destructive-text flex items-center gap-1"><XCircle className="h-4 w-4" />{t('gcVoided')}</span>
                }
              </div>
              <div>Balance: <strong>{formatCurrency(lookedUp.currentBalance)}</strong> of {formatCurrency(lookedUp.initialValue)}</div>
              {lookedUp.expiryDate && <div>Expires: {formatDateOnly(lookedUp.expiryDate)}</div>}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Input
              placeholder={t('gcSearchByCode')}
              value={search}
              onChange={e => { setSearch(e.target.value.toUpperCase()); setPage(1); }}
              className="max-w-xs font-mono"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? <SkeletonTable rows={5} cols={5} /> : cards.length === 0 ? (
            <EmptyState icon={Gift} title={t('gcNoGiftCards')} description={canCreate ? t('gcIssueFirst') : ''} />
          ) : (
            <Table mobileCards>
                <THead>
                  <Tr hover={false}>
                    <Th>{t('gcColCode')}</Th>
                    <Th>{t('gcColCustomer')}</Th>
                    <Th className="text-end">{t('gcColInitialValue')}</Th>
                    <Th className="text-end">{t('gcColBalance')}</Th>
                    <Th>{t('gcColStatus')}</Th>
                    <Th>{t('gcColExpiry')}</Th>
                    <Th className="text-end">{t('gcColActions')}</Th>
                  </Tr>
                </THead>
                <TBody>
                  {cards.map((c) => (
                    <Tr key={c.id}>
                      <Td label={t('gcColCode')} className="font-mono font-bold text-foreground">{c.code}</Td>
                      <Td label={t('gcColCustomer')} className="text-foreground text-sm">
                        {c.customer ? <Link to={`/customers/${c.customer.id}`} className="hover:underline">{c.customer.name}</Link> : '—'}
                      </Td>
                      <Td label={t('gcColInitialValue')} className="text-end text-foreground">{formatCurrency(c.initialValue)}</Td>
                      <Td label={t('gcColBalance')} className="text-end font-medium text-foreground">{formatCurrency(c.currentBalance)}</Td>
                      <Td label={t('gcColStatus')}>
                        <StatusBadge status={c.isActive ? 'ACTIVE' : 'VOIDED'} label={c.isActive ? undefined : t('gcVoided')} />
                      </Td>
                      <Td label={t('gcColExpiry')} className="text-muted-foreground text-xs">
                        {c.expiryDate ? formatDateOnly(c.expiryDate) : '—'}
                      </Td>
                      <Td label={t('gcColActions')} className="text-end">
                          <TableRowActions
                            actions={[
                              {
                                key: 'history',
                                label: t('gcHistory'),
                                icon: History,
                                onSelect: () => { setHistoryCode(c.code); setHistoryPage(1); },
                                permission: true,
                                labeled: true,
                              },
                              {
                                key: 'reload',
                                label: t('gcReload'),
                                icon: RefreshCw,
                                onSelect: () => setReloadOpen(c.code),
                                permission: canCreate && c.isActive,
                                labeled: true,
                                primary: true,
                              },
                              {
                                key: 'void',
                                label: t('gcVoid'),
                                icon: Ban,
                                onSelect: () => setVoidConfirm(c.code),
                                permission: canDelete && c.isActive,
                                variant: 'destructive',
                                labeled: true,
                              },
                            ]}
                          />
                        </Td>
                    </Tr>
                  ))}
                </TBody>
            </Table>
          )}
          <div className="px-4 py-3 border-t border-border">
            <PaginationBar page={page} pageSize={20} total={total} onPageChange={setPage} />
          </div>
        </CardContent>
      </Card>

      {/* History Dialog (T-483) */}
      <Dialog
        header={t('gcHistoryTitle', { code: historyCode })}
        visible={!!historyCode}
        onHide={() => setHistoryCode(null)}
        className="w-full max-w-3xl"
        draggable={false}
        modal
      >
        {historyLoading ? (
          <SkeletonTable rows={4} cols={5} />
        ) : (history?.data ?? []).length === 0 ? (
          <EmptyState icon={History} title={t('gcHistoryEmpty')} description={t('gcHistoryEmptyHint')} />
        ) : (
          <>
            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  <Th>{t('gcHistDate')}</Th>
                  <Th>{t('gcHistType')}</Th>
                  <Th className="text-end">{t('gcHistAmount')}</Th>
                  <Th className="text-end">{t('gcHistBalanceAfter')}</Th>
                  <Th>{t('gcHistSale')}</Th>
                </Tr>
              </THead>
              <TBody>
                {(history?.data ?? []).map((row) => (
                  <Tr key={row.id}>
                    <Td label={t('gcHistDate')} className="text-xs text-muted-foreground">{formatDate(row.createdAt)}</Td>
                    <Td label={t('gcHistType')}>{t(`gcTx_${row.type}`)}</Td>
                    <Td label={t('gcHistAmount')} className="text-end text-foreground">
                      {row.type === 'REDEEM' || row.type === 'VOID' ? '-' : '+'}{formatCurrency(row.amount)}
                    </Td>
                    <Td label={t('gcHistBalanceAfter')} className="text-end font-medium text-foreground">
                      {formatCurrency(row.balanceAfter)}
                    </Td>
                    <Td label={t('gcHistSale')} className="text-xs">
                      {row.sale ? (
                        <RecordLink type="invoice" id={row.sale.id} label={row.sale.invoiceNumber} variant="mono" className="text-xs" />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
            <div className="pt-3">
              <PaginationBar page={historyPage} pageSize={10} total={history?.total ?? 0} onPageChange={setHistoryPage} />
            </div>
          </>
        )}
      </Dialog>

      {/* Issue Dialog */}
      <FormDialog
        open={issueOpen}
        title={t('gcIssueDialogHeader')}
        width="28rem"
        submitLabel={t('gcIssueCardBtn')}
        cancelLabel={t('cancelBtn')}
        isPending={issueMutation.isPending}
        onClose={() => setIssueOpen(false)}
        onSubmit={(e) => {
          e.preventDefault();
          if (!issueForm.initialValue || parseFloat(issueForm.initialValue) <= 0) {
            toast.error(t('gcInitialValueRequired'));
            return;
          }
          issueMutation.mutate();
        }}
      >
          <div>
            <Label>{t('gcCustomCodeLabel')}</Label>
            <Input placeholder={t('gcAutoGenPlaceholder')} value={issueForm.code} onChange={e => setIssueForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} className="font-mono" />
          </div>
          <div>
            <Label>{t('gcInitialValueLabel')}</Label>
            <Input type="number" min="1" step="0.01" placeholder={t('gcInitialValuePlaceholder')} value={issueForm.initialValue} onChange={e => setIssueForm(f => ({ ...f, initialValue: e.target.value }))} />
          </div>
          <div>
            <Label>{t('gcPaymentLabel')}</Label>
            <Label className="flex items-center gap-2 mt-1 font-normal leading-normal text-foreground">
              <Checkbox
                checked={issueForm.isComplimentary}
                onChange={e => setIssueForm(f => ({ ...f, isComplimentary: e.target.checked }))}
              />
              {t('gcComplimentaryLabel')}
            </Label>
            {!issueForm.isComplimentary && (
              <Dropdown
                className="mt-2 w-full"
                value={issueForm.paymentMethod}
                onChange={e => setIssueForm(f => ({ ...f, paymentMethod: e.value }))}
                options={[
                  { label: t('gcTenderCash'), value: 'CASH' },
                  { label: t('gcTenderCard'), value: 'CARD' },
                  { label: t('gcTenderBank'), value: 'BANK_TRANSFER' },
                  { label: t('gcTenderWallet'), value: 'MOBILE_WALLET' },
                ]}
                optionLabel="label"
                optionValue="value"
              />
            )}
          </div>
          <div>
            <Label>{t('gcCustomerLabel')}</Label>
            <Dropdown
              value={issueForm.issuedToCustomerId}
              onChange={(e) => setIssueForm((f) => ({ ...f, issuedToCustomerId: (e.value as string | null) ?? '' }))}
              options={customerOptions.map((c) => ({ label: c.phone ? `${c.name} · ${c.phone}` : c.name, value: c.id }))}
              optionLabel="label"
              optionValue="value"
              filter
              onFilter={(e) => setCustomerQuery(e.filter)}
              showClear
              placeholder={t('gcCustomerPlaceholder')}
              emptyMessage={t('gcCustomerNone')}
              className="w-full"
            />
          </div>
          <div>
            <Label>{t('gcExpiryDateLabel')}</Label>
            <Input type="date" value={issueForm.expiryDate} onChange={e => setIssueForm(f => ({ ...f, expiryDate: e.target.value }))} />
          </div>
          <div>
            <Label>{t('gcNotesLabel')}</Label>
            <Input value={issueForm.notes} onChange={e => setIssueForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
      </FormDialog>

      {/* Reload Dialog */}
      <FormDialog
        open={!!reloadOpen}
        title={t('gcReloadDialogHeader', { code: reloadOpen })}
        width="24rem"
        submitLabel={t('gcReloadBtn')}
        cancelLabel={t('cancelBtn')}
        isPending={reloadMutation.isPending}
        onClose={() => { setReloadOpen(null); setReloadAmount(''); }}
        onSubmit={(e) => {
          e.preventDefault();
          if (!reloadAmount || parseFloat(reloadAmount) <= 0) {
            toast.error(t('gcReloadRequired'));
            return;
          }
          reloadMutation.mutate(reloadOpen!);
        }}
      >
        <div>
          <Label>{t('gcReloadAmountLabel')}</Label>
          <Input type="number" min="1" step="0.01" placeholder={t('gcReloadAmountPlaceholder')} value={reloadAmount} onChange={e => setReloadAmount(e.target.value)} />
        </div>
        <div>
          <Label>{t('gcPaymentLabel')}</Label>
          <Dropdown
            className="mt-1 w-full"
            value={reloadMethod}
            onChange={e => setReloadMethod(e.value)}
            options={[
              { label: t('gcTenderCash'), value: 'CASH' },
              { label: t('gcTenderCard'), value: 'CARD' },
              { label: t('gcTenderBank'), value: 'BANK_TRANSFER' },
              { label: t('gcTenderWallet'), value: 'MOBILE_WALLET' },
            ]}
            optionLabel="label"
            optionValue="value"
          />
        </div>
      </FormDialog>

      <FormDialog
        open={!!voidConfirm}
        title={t('gcVoidDialogTitle')}
        width="28rem"
        submitLabel={t('gcVoidBtn')}
        cancelLabel={t('cancelBtn')}
        isPending={voidMutation.isPending}
        onClose={() => { setVoidConfirm(null); setVoidForm({ reason: '', refund: false }); }}
        onSubmit={(e) => {
          e.preventDefault();
          if (voidForm.reason.trim().length < 3) {
            toast.error(t('gcVoidReasonRequired'));
            return;
          }
          voidMutation.mutate(voidConfirm!);
        }}
      >
        <p className="text-sm text-muted-foreground">{t('gcVoidConfirmMsg', { code: voidConfirm })}</p>
        <div>
          <Label>{t('gcVoidReasonLabel')}</Label>
          <Input value={voidForm.reason} onChange={e => setVoidForm(v => ({ ...v, reason: e.target.value }))} />
        </div>
        <Label className="flex items-center gap-2 font-normal leading-normal text-foreground">
          <Checkbox checked={voidForm.refund} onChange={e => setVoidForm(v => ({ ...v, refund: e.target.checked }))} />
          {t('gcVoidRefundLabel')}
        </Label>
        <p className="text-xs text-muted-foreground">{t('gcVoidRefundHint')}</p>
      </FormDialog>
    </div>
  );
}
